import React, { useState, useEffect } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/hooks/use-toast';
import { Loader2 } from 'lucide-react';
import {
  UpdateBookData,
  Book,
  Author,
  Publisher,
  Genre,
  booksApi,
  authorsApi,
  publishersApi,
  genresApi
} from '@/services/catalog-api';
import { transformMediaUrl } from '@/lib/utils';
import { useQueryClient } from '@tanstack/react-query';
import { catalogQueryKeys } from '@/hooks/catalog-hooks';
import Drawer from '@/components/admin/components/Drawer';

interface EditBookFormProps {
  book: Book | null;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const inputCls = "w-full px-3 py-2 border border-[var(--lib-hairline)] rounded-lg text-sm text-[var(--lib-ink)] placeholder:text-[var(--lib-ink-muted)] focus:outline-none focus:border-[var(--lib-gold)] focus:ring-2 focus:ring-[var(--lib-gold)]/15 bg-transparent";
const labelCls = "block font-mono text-[10px] uppercase tracking-wider text-[var(--lib-ink-muted)] mb-1.5";

const EditBookForm: React.FC<EditBookFormProps> = ({ book, open, onClose, onSuccess }) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [isLoading, setIsLoading] = useState(false);
  const [authors, setAuthors] = useState<Author[]>([]);
  const [publishers, setPublishers] = useState<Publisher[]>([]);
  const [genres, setGenres] = useState<Genre[]>([]);

  // Form state matching UpdateBookData interface
  const [formData, setFormData] = useState<Omit<UpdateBookData, 'author_ids' | 'genre_ids' | 'publisher_id'> & {
    author_ids: string;
    genre_ids: string;
    publisher_id: string;
    cover_image: File | null;
  }>({
    isbn: '',
    title: '',
    subtitle: '',
    publication_date: '',
    description: '',
    language: 'English',
    pages: 0,
    edition: '',
    series: '',
    series_number: 0,
    author_ids: '',
    genre_ids: '',
    publisher_id: '',
    cover_image: null
  });

  // Load dropdown data
  useEffect(() => {
    const loadDropdownData = async () => {
      try {
        const [authorsRes, publishersRes, genresRes] = await Promise.all([
          authorsApi.getAuthors({ page_size: 100 }),
          publishersApi.getPublishers({ page_size: 100 }),
          genresApi.getGenres({ page_size: 100 })
        ]);

        setAuthors(authorsRes.results);
        setPublishers(publishersRes.results);
        setGenres(genresRes.results);
      } catch (error) {
        console.error('Failed to load dropdown data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load form options. Please try again.',
          variant: 'destructive'
        });
      }
    };

    if (open) {
      loadDropdownData();
    }
  }, [open, toast]);

  // Populate form with book data when book changes
  useEffect(() => {
    if (book && open) {
      setFormData({
        isbn: book.isbn || '',
        title: book.title || '',
        subtitle: book.subtitle || '',
        publication_date: book.publication_date || '',
        description: book.description || '',
        language: book.language || 'English',
        pages: book.pages || 0,
        edition: book.edition || '',
        series: book.series || '',
        series_number: book.series_number || 0,
        author_ids: book.authors?.map(author => author.id).join(', ') || '',
        genre_ids: book.genres?.map(genre => genre.id).join(', ') || '',
        publisher_id: book.publisher_info?.id?.toString() || '',
        cover_image: null
      });
    }
  }, [book, open]);

  const resetForm = () => {
    setFormData({
      isbn: '',
      title: '',
      subtitle: '',
      publication_date: '',
      description: '',
      language: 'English',
      pages: 0,
      edition: '',
      series: '',
      series_number: 0,
      author_ids: '',
      genre_ids: '',
      publisher_id: '',
      cover_image: null
    });
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();

    if (!book) {
      toast({
        title: 'Error',
        description: 'No book selected for editing.',
        variant: 'destructive'
      });
      return;
    }

    if (!formData.title || !formData.isbn || !formData.author_ids || !formData.publisher_id) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields.',
        variant: 'destructive'
      });
      return;
    }

    setIsLoading(true);

    try {
      const bookData: UpdateBookData = {
        ...formData,
        author_ids: formData.author_ids.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id)),
        genre_ids: formData.genre_ids ? formData.genre_ids.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id)) : [],
        publisher_id: parseInt(formData.publisher_id),
        pages: formData.pages || undefined,
        series_number: formData.series_number || undefined
      };

      // Handle cover image upload (multipart/form-data) when a file is present
      if (formData.cover_image) {
        const formDataWithFile = new FormData();

        Object.entries(bookData).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          if (Array.isArray(value)) {
            value.forEach(item => formDataWithFile.append(key, item.toString()));
            return;
          }
          formDataWithFile.append(key, String(value));
        });

        formDataWithFile.append('cover_image', formData.cover_image);

        const response = await fetch(`/api/v1/catalog/books/${book.id}/`, {
          method: 'PATCH',
          credentials: 'include',
          body: formDataWithFile,
          headers: {
            'Accept': 'application/json',
            'X-Use-Cookie-Auth': 'true',
          },
        });

        const responseBody = await response.json().catch(() => null);

        if (!response.ok) {
          const detail = responseBody?.detail || responseBody || `Failed to upload cover image (${response.status})`;
          throw new Error(typeof detail === 'string' ? detail : JSON.stringify(detail));
        }

        try {
          queryClient.invalidateQueries({ queryKey: catalogQueryKeys.books.lists() });
          queryClient.invalidateQueries({ queryKey: catalogQueryKeys.books.detail(book.id) });
        } catch (e) {
          console.warn('Failed to invalidate queries after cover upload', e);
        }
      } else {
        const bookId = book.id;
        await booksApi.updateBook(bookId, bookData);

        try {
          queryClient.invalidateQueries({ queryKey: catalogQueryKeys.books.lists() });
          queryClient.invalidateQueries({ queryKey: catalogQueryKeys.books.detail(bookId) });
        } catch (e) {
          console.warn('Failed to invalidate queries after book update', e);
        }
      }

      toast({
        title: 'Success',
        description: 'Book updated successfully.'
      });

      onSuccess();
      onClose();
    } catch (error: any) {
      console.error('Failed to update book:', error);

      let errorMessage = 'Failed to update book. Please try again.';
      if (error.response?.data?.detail) {
        errorMessage = error.response.data.detail;
      } else if (error.response?.data?.isbn) {
        errorMessage = 'A book with this ISBN already exists.';
      }

      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: string, value: string | number) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Edit Book"
      width={560}
      footer={
        <div className="flex gap-2 justify-end">
          <button
            type="button"
            onClick={() => { resetForm(); onClose(); }}
            disabled={isLoading}
            className="px-4 py-2 text-sm text-[var(--lib-ink-muted)] border border-[var(--lib-hairline)] rounded-lg hover:bg-[var(--lib-surface-2)] transition-colors"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => handleSubmit()}
            disabled={isLoading || !formData.title || !formData.isbn || !formData.author_ids || !formData.publisher_id}
            className="bg-[var(--lib-gold)] hover:brightness-105 disabled:opacity-50 text-[var(--lib-on-gold)] font-medium text-sm rounded-lg px-4 py-2 transition-colors flex items-center gap-2"
          >
            {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
            Update Book
          </button>
        </div>
      }
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        {/* Basic Information */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-[var(--lib-ink)]">Basic Information</h3>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-title" className={labelCls}>Title *</Label>
              <Input
                id="edit-title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder="Enter book title"
                required
                className={inputCls}
              />
            </div>

            <div>
              <Label htmlFor="edit-subtitle" className={labelCls}>Subtitle</Label>
              <Input
                id="edit-subtitle"
                value={formData.subtitle}
                onChange={(e) => handleInputChange('subtitle', e.target.value)}
                placeholder="Subtitle (optional)"
                className={inputCls}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor="edit-isbn" className={labelCls}>ISBN *</Label>
              <Input
                id="edit-isbn"
                value={formData.isbn}
                onChange={(e) => handleInputChange('isbn', e.target.value)}
                placeholder="Enter ISBN"
                required
                className={inputCls}
              />
            </div>

            <div>
              <Label htmlFor="edit-publication_date" className={labelCls}>Publication Date *</Label>
              <Input
                id="edit-publication_date"
                type="date"
                value={formData.publication_date}
                onChange={(e) => handleInputChange('publication_date', e.target.value)}
                required
                className={inputCls}
              />
            </div>
          </div>

          {/* Cover Image */}
          <div>
            <Label htmlFor="edit-cover_image" className={labelCls}>Cover Image</Label>
            <div className="flex items-center gap-4">
              {book?.cover_image && (
                <img
                  src={transformMediaUrl(book.cover_image)}
                  alt="Current cover"
                  className="w-12 h-16 object-cover rounded border border-[var(--lib-hairline)]"
                />
              )}
              <div className="flex-1">
                <Input
                  id="edit-cover_image"
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files?.[0] || null;
                    setFormData(prev => ({ ...prev, cover_image: file }));
                  }}
                  className={inputCls}
                />
                <p className="text-xs text-[var(--lib-ink-muted)] mt-1">
                  {book?.cover_image ? 'Upload to replace current cover' : 'Upload a cover image (optional)'}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Authors and Publisher */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-[var(--lib-ink)]">Authors and Publisher</h3>

          <div>
            <Label htmlFor="edit-author_ids" className={labelCls}>Authors * (comma-separated IDs)</Label>
            <Input
              id="edit-author_ids"
              value={formData.author_ids}
              onChange={(e) => handleInputChange('author_ids', e.target.value)}
              placeholder="e.g., 1, 2, 3"
              required
              className={inputCls}
            />
            <div className="text-xs text-[var(--lib-ink-muted)] max-h-24 overflow-y-auto mt-1">
              Available: {authors.map(a => `${a.id}: ${a.full_name}`).join(', ')}
            </div>
          </div>

          <div>
            <Label htmlFor="edit-publisher_id" className={labelCls}>Publisher *</Label>
            <Select value={formData.publisher_id} onValueChange={(value) => handleInputChange('publisher_id', value)}>
              <SelectTrigger className={inputCls}>
                <SelectValue placeholder="Select publisher" />
              </SelectTrigger>
              <SelectContent>
                {publishers.map(publisher => (
                  <SelectItem key={publisher.id} value={publisher.id.toString()}>
                    {publisher.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Classification */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-[var(--lib-ink)]">Classification</h3>

          <div>
            <Label htmlFor="edit-genre_ids" className={labelCls}>Genres (comma-separated IDs)</Label>
            <Input
              id="edit-genre_ids"
              value={formData.genre_ids}
              onChange={(e) => handleInputChange('genre_ids', e.target.value)}
              placeholder="e.g., 1, 2, 3"
              className={inputCls}
            />
            <div className="text-xs text-[var(--lib-ink-muted)] max-h-24 overflow-y-auto mt-1">
              Available: {genres.map(g => `${g.id}: ${g.name}`).join(', ')}
            </div>
          </div>

          <div>
            <Label htmlFor="edit-language" className={labelCls}>Language</Label>
            <Select value={formData.language} onValueChange={(value) => handleInputChange('language', value)}>
              <SelectTrigger className={inputCls}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="English">English</SelectItem>
                <SelectItem value="Spanish">Spanish</SelectItem>
                <SelectItem value="French">French</SelectItem>
                <SelectItem value="German">German</SelectItem>
                <SelectItem value="Chinese">Chinese</SelectItem>
                <SelectItem value="Japanese">Japanese</SelectItem>
                <SelectItem value="Arabic">Arabic</SelectItem>
                <SelectItem value="Portuguese">Portuguese</SelectItem>
                <SelectItem value="Russian">Russian</SelectItem>
                <SelectItem value="Italian">Italian</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Additional Details */}
        <div className="space-y-4">
          <h3 className="text-sm font-semibold text-[var(--lib-ink)]">Additional Details</h3>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label htmlFor="edit-pages" className={labelCls}>Pages</Label>
              <Input
                id="edit-pages"
                type="number"
                min="1"
                value={formData.pages || ''}
                onChange={(e) => handleInputChange('pages', e.target.value ? parseInt(e.target.value) : 0)}
                placeholder="Pages"
                className={inputCls}
              />
            </div>

            <div>
              <Label htmlFor="edit-edition" className={labelCls}>Edition</Label>
              <Input
                id="edit-edition"
                value={formData.edition}
                onChange={(e) => handleInputChange('edition', e.target.value)}
                placeholder="1st, 2nd…"
                className={inputCls}
              />
            </div>

            <div>
              <Label htmlFor="edit-series" className={labelCls}>Series</Label>
              <Input
                id="edit-series"
                value={formData.series}
                onChange={(e) => handleInputChange('series', e.target.value)}
                placeholder="Series name"
                className={inputCls}
              />
            </div>
          </div>

          {formData.series && (
            <div className="grid grid-cols-3 gap-4">
              <div>
                <Label htmlFor="edit-series_number" className={labelCls}>Series #</Label>
                <Input
                  id="edit-series_number"
                  type="number"
                  min="1"
                  value={formData.series_number || ''}
                  onChange={(e) => handleInputChange('series_number', e.target.value ? parseInt(e.target.value) : 0)}
                  placeholder="Position"
                  className={inputCls}
                />
              </div>
            </div>
          )}

          <div>
            <Label htmlFor="edit-description" className={labelCls}>Description</Label>
            <Textarea
              id="edit-description"
              value={formData.description}
              onChange={(e) => handleInputChange('description', e.target.value)}
              placeholder="Book description or summary"
              rows={4}
              className={inputCls}
            />
          </div>
        </div>
      </form>
    </Drawer>
  );
};

export default EditBookForm;
